import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { apiGet, apiPost, apiFetch } from './api';
import { STORES_API_BASE, isNotAvailable } from './useStores';

export type SamplingEntry = {
  id: number;
  /** Hour of the slot, 0 to 23, on the store's own clock. */
  hour_slot: number;
  /**
   * Minute of the slot, 0 or 30. Slots used to be whole hours, so rows written
   * before half-hours existed come back as 0, which is what they meant.
   */
  minute_slot: number;
  item_no: string | null;
  item_descr: string | null;
  notes: string | null;
  created_by_email: string;
  created_at: string;
};

export type SamplingResponse = {
  date: string;
  entries: SamplingEntry[];
};

function samplingQKey(date: string, storeNo?: number) {
  return ['stores', 'sampling', date, storeNo ?? 'own'] as const;
}

export function useSampling(date: string, storeNo?: number): UseQueryResult<SamplingResponse, Error> {
  const qs = new URLSearchParams({ date });
  if (storeNo !== undefined) qs.set('store_no', String(storeNo));
  return useQuery<SamplingResponse, Error>({
    queryKey: samplingQKey(date, storeNo),
    queryFn: async () => {
      try {
        return await apiGet<SamplingResponse>(`${STORES_API_BASE}/sampling?${qs.toString()}`);
      } catch (err) {
        if (isNotAvailable(err)) return { date, entries: [] };
        throw err;
      }
    },
    staleTime: 30_000,
    retry: false,
  });
}

export function useAddSamplingEntry(storeNo?: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      log_date: string;
      hour_slot: number;
      /** 0 or 30. Omitted means the top of the hour. */
      minute_slot?: number;
      item_no?: string;
      item_descr?: string;
      notes?: string;
    }) =>
      apiPost<{ id: number; date: string }>(`${STORES_API_BASE}/sampling`, {
        ...body,
        ...(storeNo !== undefined ? { store_no: String(storeNo) } : {}),
      }),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: samplingQKey(variables.log_date, storeNo) });
    },
  });
}

export function useDeleteSamplingEntry(storeNo?: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => {
      const qs = storeNo !== undefined ? `?store_no=${storeNo}` : '';
      return apiFetch<{ deleted: boolean }>(`${STORES_API_BASE}/sampling/${id}${qs}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stores', 'sampling'] });
    },
  });
}
